import type { SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';

import type { TimeRangeLane, TimeSeriesChartRangeTrack } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { TRACK_GRID_INDEX, TRACK_X_AXIS_INDEX } from '../config/chartLayout';
import { trackLabelVisuals } from '../config/chartVisuals';
import type { TimeDomain } from '../utils/timeDomain';
import { buildRangeBarsOption } from './buildRangeBarsOption';
import { buildRowHighlightOption } from './buildRowHighlightOption';
import { buildTrackLabelOption } from './buildTrackLabelOption';

export type RangeTrackOption = {
  xAxis: XAXisComponentOption;
  yAxis: YAXisComponentOption;
  series: SeriesOption[];
};

type BuildRangeTrackOptionParams = {
  rangeTrack: TimeSeriesChartRangeTrack;
  highlightedLanes: TimeRangeLane[];
  domain: TimeDomain;
  yAxisIndex: number;
  theme: TimeSeriesChartTheme;
};

function buildTrackXAxis([min, max]: TimeDomain): XAXisComponentOption {
  return {
    type: 'time',
    gridIndex: TRACK_GRID_INDEX,
    min,
    max,
    axisLabel: { show: false },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    axisPointer: {
      label: { show: false },
    },
  };
}

function buildTrackYAxis(
  rangeTrack: TimeSeriesChartRangeTrack,
  highlightedLanes: TimeRangeLane[],
  theme: TimeSeriesChartTheme
): YAXisComponentOption {
  return {
    type: 'category',
    gridIndex: TRACK_GRID_INDEX,
    inverse: true,
    z: trackLabelVisuals.z,
    data: rangeTrack.lanes.map((lane) => lane.label),
    axisLabel: buildTrackLabelOption({ lanes: rangeTrack.lanes, highlightedLanes, theme }),
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
  };
}

/** Row highlights come first so the bars are drawn on top of them. */
export function buildRangeTrackOption({
  rangeTrack,
  highlightedLanes,
  domain,
  yAxisIndex,
  theme,
}: BuildRangeTrackOptionParams): RangeTrackOption {
  const highlightedLaneIds = new Set(highlightedLanes.map((lane) => lane.id));
  const xAxisIndex = TRACK_X_AXIS_INDEX;

  const rowHighlights = rangeTrack.lanes.flatMap((lane, laneIndex) =>
    highlightedLaneIds.has(lane.id)
      ? [buildRowHighlightOption({ lane, laneIndex, domain, xAxisIndex, yAxisIndex, theme })]
      : []
  );

  const bars = rangeTrack.lanes.map((lane, laneIndex) =>
    buildRangeBarsOption({ lane, laneIndex, xAxisIndex, yAxisIndex, theme })
  );

  return {
    xAxis: buildTrackXAxis(domain),
    yAxis: buildTrackYAxis(rangeTrack, highlightedLanes, theme),
    series: [...rowHighlights, ...bars],
  };
}
